import { useTranslations } from '@/i18n/use-translations';
import { useEventStore } from '@/state/stores/use-event-store';
import { AccessTime, DoorFrontOutlined, CalendarToday, Edit, SettingsOutlined } from '@mui/icons-material';
import { Box, Stack, Typography, IconButton, Button, Tooltip } from '@mui/material';
import { format, addDays } from 'date-fns';
import { useMemo } from 'react';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import Widget from '@/components/common/widget';
import ExpandableDescription from '@/components/events/expandable-description';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import type { Mode } from '@/types/entity-mode';

export default function General({
  openSettingsWithDayTiming,
  openSettings,
  modeOverride,
}: {
  openSettingsWithDayTiming: (dayIndex: number) => void;
  openSettings?: () => void;
  modeOverride?: Mode;
}) {
  const t = useTranslations();
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);
  const event = useEventStore((s) => s.event);
  const eventMode = useEventMode();
  const mode = modeOverride ?? eventMode;

  // Use mode-specific event document
  const eventToUse = useMemo(() => {
    if (mode === 'PLAN' && eventPlan) return eventPlan;
    if ((mode === 'ORGANIZE' || mode === 'PERFORM') && eventOrganize) return eventOrganize;
    return eventPlan ?? eventOrganize ?? event;
  }, [mode, eventPlan, eventOrganize, event]);

  const daysData = useMemo(() => {
    if (!eventToUse) return [];

    return eventToUse.days.map((day, index) => {
      return {
        dayNumber: index + 1,
        date: format(addDays(new Date(eventToUse.startDate), index), 'dd.MM'),
        startTime: minuteOffsetToTime(day.startOffsetMinutes, eventToUse.startDate),
        endTime: minuteOffsetToTime(day.endOffsetMinutes, eventToUse.startDate),
        openTime: minuteOffsetToTime(day.openOffsetMinutes, eventToUse.startDate),
      };
    });
  }, [eventToUse]);

  const widgetTitle = useMemo(() => {
    if (!eventToUse) return '';
    const eventTypeLabel = t(`event.types.${eventToUse.type}`);
    const locationText = eventToUse.location?.trim();
    const typeWithOptionalLocation = locationText
      ? t('event.typeInLocation', { eventType: eventTypeLabel, location: locationText })
      : eventTypeLabel;
    if (eventToUse.durationDays === 1) return typeWithOptionalLocation;
    return `${t('common.dayWithCount', { count: eventToUse.durationDays })} ${typeWithOptionalLocation}`;
  }, [eventToUse, t]);

  if (!eventToUse) return null;

  return (
    <Widget
      data-testid="event-basic-info"
      title={widgetTitle}
      iconButtons={
        openSettings ? (
          <Tooltip title={mode === 'ORGANIZE' ? t('event.switchToPlanToEditSettings') : t('event.settings')}>
            <span style={{ display: 'inline-block' }}>
              <IconButton
                data-testid="event-settings-button"
                disabled={isOrganizeMode(mode)}
                size="small"
                onClick={openSettings}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <SettingsOutlined />
              </IconButton>
              <Button
                data-testid="event-settings-button-full"
                disabled={isOrganizeMode(mode)}
                size="small"
                color="secondary"
                variant="outlined"
                startIcon={<SettingsOutlined />}
                onClick={openSettings}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                {t('event.settings')}
              </Button>
            </span>
          </Tooltip>
        ) : undefined
      }
    >
      {eventToUse.title && eventToUse.title.trim() !== '' && (
        <Stack gap={0}>
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{t('event.eventTitle')}</Box>
          <Typography variant="body1">{eventToUse.title}</Typography>
        </Stack>
      )}
      {eventToUse.description && eventToUse.description.trim() !== '' && (
        <Stack gap={0} sx={{ mt: eventToUse.title && eventToUse.title.trim() !== '' ? 1 : 0 }}>
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{t('event.description')}</Box>
          <ExpandableDescription description={eventToUse.description} />
        </Stack>
      )}

      <Stack gap={0} sx={{ mt: 1 }}>
        <Stack direction="row" justifyContent="left" alignItems="center">
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{t('common.eventTiming')}</Box>
          <IconButton
            size="small"
            disabled={isOrganizeMode(mode)}
            onClick={() => openSettingsWithDayTiming(0)}
            aria-label={t('common.edit')}
          >
            <Edit fontSize="small" color={isOrganizeMode(mode) ? 'disabled' : 'action'} />
          </IconButton>
        </Stack>
        {daysData.map((day, index) => (
          <Stack
            data-testid={`day-${index}`}
            key={index}
            direction="row"
            spacing={1}
            justifyContent={eventToUse.multiFlow === false ? 'flex-start' : 'space-between'}
            onClick={() => openSettingsWithDayTiming(index)}
            alignItems="center"
            sx={{
              'cursor': 'pointer',
              'borderRadius': 1,
              'p': 0.5,
              'transition': 'all 0.1s ease-in-out',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {eventToUse.multiFlow !== false && (
              <Typography variant="body2">
                {t('common.day')} {day.dayNumber}
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack direction="row" spacing={0.3} alignItems="center">
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2">{day.date}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.3} alignItems="center">
                <DoorFrontOutlined fontSize="small" color="action" />
                <Typography variant="body2">{day.openTime}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.3} alignItems="center">
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2">
                  {day.startTime}-{day.endTime}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Widget>
  );
}
